import React, { useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Line,
  LabelList,
} from "recharts";
import { Box, Typography, useTheme, CircularProgress } from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../stores";
import { getLeadsConversion } from "../../features/lead-module/leadAction";

const StyledChartCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  borderRadius: 1.5,
  boxShadow: theme.shadows[3],
  backgroundColor: "#fff",
  transition: "all 0.3s ease-in-out",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  "&:hover": {
    transform: "translateY(-2px)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5, 2),
  },
  [theme.breakpoints.down("xs")]: {
    padding: theme.spacing(1, 1.5),
  },
}));

const ChartContainer = styled(Box)(({ theme }) => ({
  height: "360px",
  width: "100%",
  position: "relative",
  [theme.breakpoints.down("md")]: {
    height: "320px",
  },
  [theme.breakpoints.down("sm")]: {
    height: "280px",
  },
  [theme.breakpoints.down("xs")]: {
    height: "250px",
  },
}));

const NoDataContainer = styled(Box)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: theme.palette.grey[100],
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(2),
  textAlign: "center",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5),
  },
}));

const CustomTooltip = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(1, 1.5),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[3],
  border: `1px solid ${theme.palette.divider}`,
  color: theme.palette.text.primary,
  maxWidth: "220px",
  [theme.breakpoints.down("sm")]: {
    maxWidth: "180px",
    padding: theme.spacing(0.75, 1),
  },
}));

// Add the LoaderContainer and LoaderText styled components
const LoaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  width: "100%",
  minHeight: "300px",
}));

const LoaderText = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(2),
  fontSize: "0.9rem",
  color: "#4b5b5d",
  fontWeight: 500,
}));

const BarChartCard = () => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const { leadsConversion, leadsConversionLoading, leadsConversionError } =
    useSelector((state: RootState) => state.lead);

  useEffect(() => {
    dispatch(getLeadsConversion() as any);
  }, [dispatch]);

  const chartData =
    leadsConversion?.stats?.map((stat) => ({
      name: stat.month.slice(0, 3),
      leads: stat.totalLeads,
      conversions: stat.totalConversions,
    })) || [];

  const CustomTooltipContent = ({
    active,
    payload,
    label,
  }: {
    active?: boolean;
    payload?: Array<{
      color?: string;
      name?: string;
      value?: number;
    }>;
    label?: string;
  }) => {
    if (active && payload && payload.length) {
      return (
        <CustomTooltip>
          <Typography
            variant="subtitle2"
            fontWeight={600}
            mb={0.5}
            sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
          >
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Box
              key={`tooltip-${index}`}
              sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: entry.color,
                  mr: 1,
                  borderRadius: "2px",
                }}
              />
              <Typography
                variant="body2"
                sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
              >
                {entry.name}:{" "}
                <b>
                  {entry.value !== undefined
                    ? entry.value.toLocaleString()
                    : "-"}
                </b>
              </Typography>
            </Box>
          ))}
        </CustomTooltip>
      );
    }
    return null;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      style={{ width: "100%", height: "100%" }}
    >
      <StyledChartCard>
        <Typography variant="h6" fontWeight={600} color="#4b5b5d">
          Analytics & Activity{" "}
          {leadsConversion?.year && `(${leadsConversion.year})`}
        </Typography>
        {leadsConversionLoading ? (
          <LoaderContainer>
            <CircularProgress
              size={40}
              thickness={4}
              sx={{
                color: "#4b5b5d",
              }}
            />
            <LoaderText>Loading analytics data...</LoaderText>
          </LoaderContainer>
        ) : leadsConversionError ? (
          <NoDataContainer>
            <Typography
              variant="body1"
              color="error"
              fontWeight={500}
              sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
            >
              {leadsConversionError}
            </Typography>
          </NoDataContainer>
        ) : !leadsConversion || chartData.length === 0 ? (
          <NoDataContainer>
            <Typography
              variant="body1"
              color="text.secondary"
              fontWeight={500}
              sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
            >
              No leads data available
            </Typography>
          </NoDataContainer>
        ) : (
          <ChartContainer>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={chartData}
                margin={{
                  top: 20,
                  right: theme.breakpoints.down("sm") ? 10 : 20,
                  left: theme.breakpoints.down("sm") ? -10 : 0,
                  bottom: 10,
                }}
                barSize={theme.breakpoints.down("sm") ? 35 : 25}
              >
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: theme.breakpoints.down("sm") ? 10 : 12 }}
                  interval={0}
                />
                <YAxis
                  tick={{ fontSize: theme.breakpoints.down("sm") ? 10 : 12 }}
                  width={theme.breakpoints.down("sm") ? 40 : 50}
                />
                <Tooltip content={<CustomTooltipContent />} />
                <Legend
                  verticalAlign="top"
                  align="right"
                  layout="horizontal"
                  wrapperStyle={{
                    paddingBottom: theme.spacing(
                      theme.breakpoints.down("sm") ? 3 : 2
                    ),
                    fontSize: theme.breakpoints.down("sm")
                      ? "0.85rem"
                      : "0.95rem",
                  }}
                />
                <Bar
                  dataKey="leads"
                  name="Number of Leads"
                  fill="#4b5b5d"
                  isAnimationActive={true}
                  animationDuration={800}
                >
                  <LabelList
                    dataKey="leads"
                    position="top"
                    fontSize={theme.breakpoints.down("sm") ? 10 : 12}
                    fill="#4b5b5d"
                    dy={-10}
                    style={{ textAnchor: "middle" }}
                  />
                </Bar>
                <Line
                  type="basis"
                  dataKey="conversions"
                  name="Number of Conversions"
                  stroke="#758d8e"
                  strokeWidth={2}
                  dot={{ r: 2 }}
                  isAnimationActive={true}
                  animationDuration={1000}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        )}
      </StyledChartCard>
    </motion.div>
  );
};

export default BarChartCard;
